/**
 * TOADS scroll reveal.
 *
 * The client asked for scroll-down reveals rather than horizontal carousels, so
 * this is the one motion primitive the whole site shares.
 *
 * Deliberate choices:
 * - Elements ship VISIBLE. The hidden state is only applied once this script is
 *   running and has confirmed it will animate, so a JS failure, an old browser,
 *   or a blocked asset can never leave a section invisible.
 * - Honours prefers-reduced-motion by doing nothing at all.
 * - IntersectionObserver only; no scroll listeners, no layout thrash.
 * - Unobserves after reveal, so long collection pages don't accumulate work.
 * - Re-runs on Shopify section reloads so the theme editor doesn't show blanks.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const reveal = (el) => {
    el.setAttribute('data-toads-revealed', '');
  };

  let observer = null;

  const getObserver = () => {
    if (observer) return observer;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      },
      // Fire slightly before the element reaches the fold so the motion reads
      // as "already in progress" rather than "triggered by my scrolling".
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );
    return observer;
  };

  const init = (root = document) => {
    const targets = root.querySelectorAll('[data-toads-reveal]:not([data-toads-reveal-ready])');
    if (!targets.length) return;

    if (REDUCED.matches) {
      // Mark as ready so we don't re-scan, but never hide or animate.
      targets.forEach((el) => {
        el.setAttribute('data-toads-reveal-ready', '');
        reveal(el);
      });
      return;
    }

    const io = getObserver();

    targets.forEach((el) => {
      // Stagger children of a shared group for a sense of cascade.
      const group = el.closest('[data-toads-reveal-group]');
      if (group) {
        const siblings = [...group.querySelectorAll('[data-toads-reveal]')];
        const i = siblings.indexOf(el);
        if (i > 0) el.style.setProperty('--toads-reveal-delay', `${Math.min(i, 6) * 70}ms`);
      }

      // Anything already on screen at load is never hidden in the first place.
      // Two reasons: animating the hero headline in delays the largest paint
      // for no benefit, and a rAF-based fade is unreliable in a background or
      // throttled tab — which is exactly where a stuck opacity:0 would hide
      // the whole hero. Below-the-fold elements get the hidden state and the
      // observer.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        reveal(el);
        return;
      }

      el.setAttribute('data-toads-reveal-ready', '');
      io.observe(el);
    });

    // Belt and braces: if anything is still hidden well after load — an
    // observer that never fired, a container with an odd display value — show
    // it. Content being visible always beats content being animated.
    window.setTimeout(() => {
      root
        .querySelectorAll('[data-toads-reveal-ready]:not([data-toads-revealed])')
        .forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight) reveal(el);
        });
    }, 2500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
